import { useEffect, useState, type ReactNode } from 'react'
import { useSearch } from '@tanstack/react-router'
import {
  BarChart3,
  CalendarDays,
  Clock,
  CreditCard,
  Lock,
  Package,
  Percent,
  ReceiptText,
  ShieldCheck,
  UserCog,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReportRange } from '@/hooks/useReportRange'
import { useBusinessName } from '@/hooks/useBusinessName'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'
import { DateRangeFilter } from './DateRangeFilter'
import { OverviewTab } from './OverviewTab'
import { DailySalesTab } from './DailySalesTab'
import { HoursTab } from './HoursTab'
import { ItemsTab } from './ItemsTab'
import { TaxSummaryTab } from './TaxSummaryTab'
import { StaffPerformanceTab } from './StaffPerformanceTab'
import { RefundLogsTab } from './RefundLogsTab'
import { CardPromosTab } from './CardPromosTab'
import { FiscalQueueTab } from './FiscalQueueTab'
import { DayHistoryPage } from '@/components/admin/dayclose/DayHistoryPage'
import {
  ReportsExportOutlet,
  ReportsExportSlotProvider,
} from './ReportsExportSlot'
import type { ReportTab, ReportsSearch } from './reportsRouteSearch'

type TabId = ReportTab

const TABS: { id: TabId; label: string; icon: ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'daily', label: 'Daily Sales', icon: <CalendarDays className="h-4 w-4" /> },
  { id: 'hours', label: 'Hours', icon: <Clock className="h-4 w-4" /> },
  { id: 'items', label: 'Items', icon: <Package className="h-4 w-4" /> },
  { id: 'staff', label: 'Staff', icon: <UserCog className="h-4 w-4" /> },
  { id: 'tax', label: 'Tax', icon: <Percent className="h-4 w-4" /> },
  { id: 'day-close', label: 'Day Close', icon: <Lock className="h-4 w-4" /> },
  { id: 'refunds', label: 'Refund Logs', icon: <ReceiptText className="h-4 w-4" /> },
  { id: 'card-promos', label: 'Card Promos', icon: <CreditCard className="h-4 w-4" /> },
  { id: 'fiscal', label: 'Fiscal Queue', icon: <ShieldCheck className="h-4 w-4" /> },
]

/**
 * The Reports & Analytics page shell. Owns the global date-range filter
 * (mirrored to URL state) and the tab navigation. Each tab is a small,
 * data-loading sibling that uses the same range hook.
 *
 * Per-day order browsing (PRA, guest receipts) lives under **Daily Sales**:
 * use “Drill in” on a row or `?tab=daily&day=YYYY-MM-DD` in the URL.
 */
export function ReportsShell() {
  const range = useReportRange()
  // `?tab=` and `?day=` query params — see routes/admin/reports.tsx
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch
  const [selectedTab, setTab] = useState<TabId>(search.tab ?? 'overview')
  // Card promos are a Marketing Suite feature; the report goes with the
  // suite on deployments that hide it (ENABLED_MODULES).
  const modules = useModuleVisibility()
  const isTabShown = (id: TabId) => id !== 'card-promos' || modules.isEnabled('marketing')
  const tabs = TABS.filter((t) => isTabShown(t.id))
  const tab: TabId = isTabShown(selectedTab) ? selectedTab : 'overview'
  useEffect(() => {
    if (search.tab && search.tab !== selectedTab) setTab(search.tab)
    // Intentionally only react to URL-driven tab changes — local clicks
    // shouldn't trigger this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.tab])

  // Pulled from Admin → Settings via the shared `useBusinessName` hook,
  // which also drives the PDF-export footer (printReport.ts) and the CSV
  // exports' brand row (backend reports.go). Single source of truth keeps
  // every surface — header, PDF, CSV — in agreement after a rename.
  // Returns null when neither restaurant_name nor receipt_business_name is
  // configured, so we can drop the trailing "for X" cleanly on a fresh
  // install instead of rendering an awkward placeholder.
  const businessName = useBusinessName()
  const subtitle = businessName
    ? `Sales, items and more for ${businessName}`
    : 'Sales, items and more'

  return (
    <ReportsExportSlotProvider>
      <div className="p-4 sm:p-6 space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Reports &amp; Analytics</h1>
            <p className="mt-1 text-muted-foreground">{subtitle}</p>
          </div>
          {tab !== 'day-close' && tab !== 'fiscal' && (
            <div className="lg:pt-1">
              <DateRangeFilter range={range} />
            </div>
          )}
        </div>

        {/* Tabs row + export outlet share one horizontal band. The outlet
            renders OUTSIDE the muted tab container so the Export button
            visually reads as a page action, not as another tab. */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
            {tabs.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                  tab === t.id
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.icon}
                {t.label}
              </button>
            ))}
          </div>
          <ReportsExportOutlet />
        </div>

        {tab === 'overview' && <OverviewTab range={range} />}
        {tab === 'daily' && <DailySalesTab range={range} />}
        {tab === 'hours' && <HoursTab range={range} />}
        {tab === 'items' && <ItemsTab range={range} />}
        {tab === 'staff' && <StaffPerformanceTab range={range} />}
        {tab === 'tax' && <TaxSummaryTab range={range} />}
        {tab === 'day-close' && <DayHistoryPage />}
        {tab === 'refunds' && <RefundLogsTab range={range} />}
        {tab === 'card-promos' && <CardPromosTab range={range} />}
        {tab === 'fiscal' && <FiscalQueueTab range={range} />}
      </div>
    </ReportsExportSlotProvider>
  )
}
